import React, { useEffect, useRef, useState } from 'react';
// 统一组件尚未封装 Toast，使用 antd-v5 的 message。
import { message } from 'antd-v5';
import { Button, Input, Modal, Table } from '@/components';
import { getStallProductCorrectionPage, batchDeleteStallProductCorrection } from '@/state/request/stall';
import './style.less';

const initialQuery = { keyword: '', pageNo: 1, pageSize: 30 };

export default function StallProductCorrection(props) {
  return <CorrectionContent key={props.stallId} {...props} />;
}

function CorrectionContent({ stallId }) {
  const validStall = Number.isSafeInteger(Number(stallId)) && Number(stallId) > 0;
  const [keyword, setKeyword] = useState('');
  const [query, setQuery] = useState(initialQuery);
  const [data, setData] = useState({ records: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [error, setError] = useState('');
  const [revision, setRevision] = useState(0);
  const [modal, contextHolder] = Modal.useModal();
  const [messageApi, messageContextHolder] = message.useMessage();
  const mounted = useRef(false);
  const deletionLock = useRef(false);
  const confirmation = useRef(null);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      if (confirmation.current) confirmation.current.destroy();
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!validStall) return undefined;
    setLoading(true);
    setError('');
    Promise.resolve()
      .then(() => getStallProductCorrectionPage(stallId, query))
      .then((response) => {
        if (cancelled) return;
        if (response?.code !== 200) throw new Error(response?.msg || '查询失败，请重新查询');
        const page = response.data || {};
        const total = page.totalSize || 0;
        const pageSize = page.pageSize || query.pageSize;
        const lastPage = Math.max(1, Math.ceil(total / pageSize));
        if (query.pageNo > lastPage) {
          setQuery({ ...query, pageNo: lastPage });
          return;
        }
        setData({ records: page.pageList || [], total, pageNo: page.pageNo || query.pageNo, pageSize });
      })
      .catch((err) => {
        if (!cancelled) {
          setData({ records: [], total: 0 });
          setError(err.message || '查询失败，请重新查询');
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [validStall, stallId, query, revision]);

  const search = (value) => {
    if (!validStall || loading || deleting || deletionLock.current) return;
    setSelectedRowKeys([]);
    setQuery({ ...query, keyword: value.trim(), pageNo: 1 });
  };

  const confirmDelete = (record) => {
    if (!validStall || loading || deleting || deletionLock.current) return;
    const ids = record ? [record.id] : [...selectedRowKeys];
    if (!ids.length) return;
    deletionLock.current = true;
    setDeleting(true);
    const release = () => {
      deletionLock.current = false;
      confirmation.current = null;
      if (mounted.current) setDeleting(false);
    };
    confirmation.current = modal.confirm({
      title: record ? '确认删除货品纠正规则？' : `确认删除选中的 ${ids.length} 条货品纠正规则？`,
      content: record
        ? <span>原识别词：{record.sourceWord}<br />纠正为：{record.targetWord}</span>
        : '删除后，这些货品纠正规则将不再生效。',
      okText: '确认删除',
      cancelText: '取消',
      okType: 'danger',
      onCancel: release,
      onOk: async () => {
        if (!mounted.current) return;
        setDeleting(true);
        setError('');
        try {
          const response = await batchDeleteStallProductCorrection(stallId, ids);
          if (!mounted.current) return;
          if (response?.code !== 200 || response.data !== true) {
            setError(response?.msg || '删除失败，请重试');
            return;
          }
          setSelectedRowKeys((keys) => keys.filter((id) => !ids.includes(id)));
          messageApi.success(`成功删除 ${ids.length} 条`, 2);
          setRevision((value) => value + 1);
        } catch (err) {
          if (mounted.current) setError(err.message || '删除失败，请重试');
        } finally {
          release();
        }
      },
    });
  };

  const disabled = !validStall || loading || deleting;
  return (
    <div className='stall-product-correction'>
      {contextHolder}
      {messageContextHolder}
      {!validStall && <p className='stall-product-correction-error' role='alert'>商行 ID 无效，请重新进入商行详情</p>}
      <form className='stall-product-correction-search' onSubmit={(event) => {
        event.preventDefault();
        search(keyword);
      }}>
        <Input
          aria-label='搜原识别词或纠正内容'
          placeholder='搜原识别词或纠正内容'
          value={keyword}
          allowClear
          disabled={disabled}
          onChange={(event) => setKeyword(event.target.value)}
        />
        <Button type='primary' htmlType='submit' disabled={disabled}>查询</Button>
        <Button disabled={disabled} onClick={() => { setKeyword(''); search(''); }}>重置</Button>
        <Button danger disabled={disabled || !selectedRowKeys.length} onClick={() => confirmDelete()}>批量删除</Button>
        <span className='stall-product-correction-selected' role='status'>已选择 {selectedRowKeys.length} 条</span>
        <Button type='link' disabled={disabled || !selectedRowKeys.length} onClick={() => setSelectedRowKeys([])}>清空选择</Button>
      </form>
      {error && <p className='stall-product-correction-error' role='alert'>{error}</p>}
      <Table
        rowKey='id'
        loading={loading}
        dataSource={data.records}
        rowSelection={{
          selectedRowKeys,
          preserveSelectedRowKeys: true,
          onChange: (keys) => { if (!disabled) setSelectedRowKeys(keys); },
          getCheckboxProps: () => ({ disabled }),
        }}
        columns={[
          { title: '原识别词', dataIndex: 'sourceWord', key: 'sourceWord' },
          { title: '纠正为', dataIndex: 'targetWord', key: 'targetWord' },
          { title: '操作', key: 'action', width: 100, render: (_, record) => (
            <Button type='link' danger disabled={disabled} onClick={() => confirmDelete(record)}>删除</Button>
          ) },
        ]}
        pagination={{
          current: data.pageNo || query.pageNo,
          pageSize: data.pageSize || query.pageSize,
          total: data.total,
          disabled,
          showSizeChanger: false,
          onChange: (pageNo) => {
            if (!disabled) setQuery({ ...query, pageNo });
          },
        }}
      />
    </div>
  );
}
